/**
 * repair-threads — the single definition of which Conversation thread a Repair Detail
 * timeline item belongs to, and of which items count as "unread candidates" for a landlord
 * (RA-UNREAD-DEF). Shared by apps/api (unread counts) and apps/landlord (thread rendering) so
 * the partition and the unread predicate can never drift apart.
 */
/** Rows older than this never count as unread — the cutoff that prevents a backlog flood on
 *  first deploy (RA-UNREAD-DEF). */
export const CASE_THREAD_UNREAD_EPOCH = '2026-10-02T00:00:00.000Z';
/** The landlord/agent notes thread key. Party threads are `tenant:<id>` / `vendor:<id>`. */
export const REPAIR_THREAD_AGENT = 'agent';
/** CaseEvent types that can be an unread candidate (a message in a thread). */
export const REPAIR_MESSAGE_EVENT_TYPES = [
    'case.comment_added',
    'case.message_sent',
    'case.message_received',
];
/** Actor kinds that can author an unread candidate (the counterparties of a thread). Used by
 *  apps/api to pre-filter candidate rows in the query. */
export const UNREAD_CANDIDATE_ACTOR_KINDS = ['tenant', 'vendor', 'agent'];
/** RA-CONVERSATION-THREADS — which thread (`'agent'` or a `${partyType}:${id}` key) a timeline
 *  item belongs to. Message events are keyed directly off their own recipient/sender identity
 *  (payload.recipient_type + recipient_id for an outbound `case.message_sent`; actorKind +
 *  actorId for an inbound `case.message_received`).
 *
 *  `case.comment_added` is the platform's side-channel comment, so a landlord/agent one is an
 *  Agent-notes entry — BUT a tenant's (or vendor's) own in-app post is ALSO stored as a
 *  `case.comment_added` with that party as `actorKind`/`actorId` (spec US-44,
 *  RA-TENANT-TIMELINE) and belongs in their thread.
 *
 *  A vendor-tagged lifecycle event (payload.vendor_id) surfaces in that vendor's own thread.
 *  Every other case-level lifecycle event with no party/recipient surfaces in the 'agent'
 *  thread only. */
export function eventThreadKey(item) {
    if (item.eventType === 'case.comment_added') {
        if ((item.actorKind === 'tenant' || item.actorKind === 'vendor') && item.actorId) {
            return `${item.actorKind}:${item.actorId}`;
        }
        return REPAIR_THREAD_AGENT;
    }
    if (item.eventType === 'case.message_sent') {
        const recipientType = item.payload?.recipient_type;
        const recipientId = item.payload?.recipient_id;
        if ((recipientType === 'tenant' || recipientType === 'vendor') &&
            typeof recipientId === 'string') {
            return `${recipientType}:${recipientId}`;
        }
        return REPAIR_THREAD_AGENT;
    }
    if (item.eventType === 'case.message_received') {
        if ((item.actorKind === 'tenant' || item.actorKind === 'vendor') && item.actorId) {
            return `${item.actorKind}:${item.actorId}`;
        }
        return REPAIR_THREAD_AGENT;
    }
    const vendorId = item.payload?.vendor_id;
    if (typeof vendorId === 'string') {
        return `vendor:${vendorId}`;
    }
    return REPAIR_THREAD_AGENT;
}
/** RA-UNREAD-DEF — whether an item in `threadKey` was authored by that thread's counterparty
 *  (and so can be unread for the landlord). The 'agent' thread's counterparty is the agent;
 *  `tenant:<id>` / `vendor:<id>` match the actor kind AND id. Landlord-authored and any other
 *  item is never unread. */
export function isCounterpartyAuthored(item, threadKey) {
    if (threadKey === REPAIR_THREAD_AGENT)
        return item.actorKind === 'agent';
    const sep = threadKey.indexOf(':');
    if (sep < 0)
        return false;
    const kind = threadKey.slice(0, sep);
    const id = threadKey.slice(sep + 1);
    if (kind !== 'tenant' && kind !== 'vendor')
        return false;
    return item.actorKind === kind && item.actorId === id;
}
/** RA-UNREAD-DEF — the single unread-candidate predicate: a message event in `threadKey`
 *  authored by that thread's counterparty. (Recency vs the epoch / read cursor is applied by
 *  the caller.) */
export function isUnreadCandidate(item, threadKey) {
    return (REPAIR_MESSAGE_EVENT_TYPES.includes(item.eventType) &&
        isCounterpartyAuthored(item, threadKey));
}
//# sourceMappingURL=repair-threads.js.map